import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  sendToastMessage,
  Text,
} from "@microbeta/ui";
import type React from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiLayers, FiMap, FiMapPin } from "react-icons/fi";
import type { GeoJSONFeatureCollection } from "../../../utils/geofileUtils";
import {
  isSupportedGeoFileType,
  processGeoFile,
  SUPPORTED_GEO_FILE_TYPES,
  validateareaGeoData,
} from "../../../utils/geofileUtils";
import { FileDropzone } from "../FileDropZone";
import styles from "./GeoFileUploader.module.scss";

interface GeoFileUploaderProps {
  onUploadComplete: (geoData: GeoJSONFeatureCollection[]) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
  multiple?: boolean;
}

interface GeoDataSummary {
  totalFeatures: number;
  points: number;
  lines: number;
  polygons: number;
  tracks: number;
  waypoints: number;
}

export const GeoFileUploader: React.FC<GeoFileUploaderProps> = ({
  onUploadComplete,
  label,
  className = "",
  disabled = false,
  multiple = false,
}) => {
  const { t: _t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [processedGeoData, setProcessedGeoData] = useState<
    GeoJSONFeatureCollection[]
  >([]);
  const [geoDataSummary, setGeoDataSummary] = useState<GeoDataSummary | null>(
    null
  );
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);

  const validateFile = useCallback((file: File): boolean => {
    if (!isSupportedGeoFileType(file.name)) {
      return false;
    }

    // 10MB limit for geo files
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return false;
    }

    return true;
  }, []);

  const handleFilesSelect = useCallback(async (files: File[]) => {
    if (!files || files.length === 0) return;

    setIsProcessing(true);
    setFileNames(files.map((f) => f.name));

    try {
      const processedDataArray = await Promise.all(
        files.map((file) => processGeoFile(file))
      );

      const totalSummary: GeoDataSummary = {
        totalFeatures: 0,
        points: 0,
        lines: 0,
        polygons: 0,
        tracks: 0,
        waypoints: 0,
      };
      const allWarnings: string[] = [];
      const validData: GeoJSONFeatureCollection[] = [];

      processedDataArray.forEach((geoData) => {
        const validation = validateareaGeoData(geoData);
        if (validation.isValid) {
          validData.push(geoData);
          totalSummary.totalFeatures += validation.summary.totalFeatures;
          totalSummary.points += validation.summary.points;
          totalSummary.lines += validation.summary.lines;
          totalSummary.polygons += validation.summary.polygons;
          totalSummary.tracks += validation.summary.tracks;
          totalSummary.waypoints += validation.summary.waypoints;
          allWarnings.push(...validation.warnings);
        } else {
          sendToastMessage(
            "Geo File Error",
            `A file was skipped because it does not contain valid geographic data.`,
            "warning"
          );
        }
      });

      if (validData.length > 0) {
        setProcessedGeoData(validData);
        setGeoDataSummary(totalSummary);
        setValidationWarnings(allWarnings);
        setShowPreview(true);
      } else {
        sendToastMessage(
          "Geo File Error",
          "None of the uploaded files contain valid geographic data.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error processing geo file(s):", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to process geo file(s)";
      sendToastMessage("Geo File Error", errorMessage, "error");
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleConfirmUpload = useCallback(() => {
    if (processedGeoData.length > 0) {
      onUploadComplete(processedGeoData);
      setShowPreview(false);
      setProcessedGeoData([]);
      setGeoDataSummary(null);
      setValidationWarnings([]);
      setFileNames([]);
      sendToastMessage(
        "Success",
        "Geo data has been uploaded successfully!",
        "success"
      );
    }
  }, [processedGeoData, onUploadComplete]);

  const handleCancelUpload = useCallback(() => {
    setShowPreview(false);
    setProcessedGeoData([]);
    setGeoDataSummary(null);
    setValidationWarnings([]);
    setFileNames([]);
  }, []);

  return (
    <>
      <FileDropzone
        onFileSelect={(file) => handleFilesSelect([file])}
        onFilesSelect={handleFilesSelect}
        label={label || "Drop GPX, GeoJSON, or KML file(s) here"}
        hint={`Supported formats: ${SUPPORTED_GEO_FILE_TYPES.join(", ")}`}
        className={className}
        disabled={disabled || isProcessing}
        uploadProgress={isProcessing ? 50 : null}
        fileName={isProcessing ? fileNames.join(", ") : null}
        accept={SUPPORTED_GEO_FILE_TYPES.join(",")}
        validateFile={validateFile}
        multiple={multiple}
      />

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className={styles.previewDialog}>
          <DialogTitle>Preview Geo Data</DialogTitle>

          {geoDataSummary && (
            <div className={styles.summaryContainer}>
              <Text variant="medium" className={styles.summaryTitle}>
                Files: {fileNames.join(", ")}
              </Text>

              <div className={styles.summaryGrid}>
                <div className={styles.summaryItem}>
                  <FiLayers className={styles.summaryIcon} />
                  <div>
                    <Text variant="small" className={styles.summaryLabel}>
                      Total Features
                    </Text>
                    <Text variant="medium" className={styles.summaryValue}>
                      {geoDataSummary.totalFeatures}
                    </Text>
                  </div>
                </div>

                <div className={styles.summaryItem}>
                  <FiMapPin className={styles.summaryIcon} />
                  <div>
                    <Text variant="small" className={styles.summaryLabel}>
                      Waypoints
                    </Text>
                    <Text variant="medium" className={styles.summaryValue}>
                      {geoDataSummary.waypoints}
                    </Text>
                  </div>
                </div>

                <div className={styles.summaryItem}>
                  <FiMap className={styles.summaryIcon} />
                  <div>
                    <Text variant="small" className={styles.summaryLabel}>
                      Tracks
                    </Text>
                    <Text variant="medium" className={styles.summaryValue}>
                      {geoDataSummary.tracks}
                    </Text>
                  </div>
                </div>
              </div>

              <div className={styles.detailsGrid}>
                <Text variant="small">Points: {geoDataSummary.points}</Text>
                <Text variant="small">Lines: {geoDataSummary.lines}</Text>
                <Text variant="small">Polygons: {geoDataSummary.polygons}</Text>
              </div>

              {validationWarnings.length > 0 && (
                <div className={styles.warningsContainer}>
                  <Text variant="small" className={styles.warningsTitle}>
                    Warnings:
                  </Text>
                  {validationWarnings.map((warning) => (
                    <Text
                      key={warning}
                      variant="small"
                      className={styles.warningItem}
                    >
                      • {warning}
                    </Text>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className={styles.dialogActions}>
            <Button variant="secondary" onClick={handleCancelUpload}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmUpload}
              disabled={processedGeoData.length === 0}
            >
              Upload Geo Data
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
